
import { supabase } from "@/integrations/supabase/client";
import { ScanditRefs } from './cleanupUtils';

export const initializeScanditSDK = async (
  refs: ScanditRefs,
  setInitializationStep: (step: string) => void,
  setError: (error: string | null) => void,
  setIsInitialized: (initialized: boolean) => void
) => {
  try {
    console.log('🚀 Initializing Scandit Web DataCapture...');
    setInitializationStep('Fetching license key...');
    setError(null);
    setIsInitialized(false);
    
    const { data, error: secretError } = await supabase.functions.invoke('get-scandit-license');
    
    if (secretError) {
      throw new Error(`Failed to fetch license: ${secretError.message}`);
    }
    
    if (!data?.licenseKey) {
      throw new Error('No license key returned from Supabase function');
    }
    
    const licenseKey = data.licenseKey;
    console.log('✅ License key retrieved successfully');
    
    setInitializationStep('Loading Scandit SDK...');
    
    const [ScanditCore, ScanditBarcode] = await Promise.all([
      import('@scandit/web-datacapture-core'),
      import('@scandit/web-datacapture-barcode')
    ]);
    
    refs.ScanditCoreRef.current = ScanditCore;
    refs.ScanditBarcodeRef.current = ScanditBarcode;
    
    setInitializationStep('Configuring SDK...');
    
    await ScanditCore.configure({
      licenseKey: licenseKey,
      libraryLocation: 'https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@7.3.0/sdc-lib/',
      moduleLoaders: [ScanditBarcode.barcodeCaptureLoader()]
    });
    
    setInitializationStep('Creating data capture context...');
    const context = await ScanditCore.DataCaptureContext.create();
    refs.contextRef.current = context;
    
    setInitializationStep('Setting up camera...');
    const camera = ScanditCore.Camera.default;
    const cameraSettings = camera.settings;
    cameraSettings.preferredResolution = ScanditCore.VideoResolution.HD;
    cameraSettings.zoomFactor = 2.0;
    
    await context.setFrameSource(camera);
    refs.cameraRef.current = camera;
    
    setInitializationStep('✅ Scandit ready!');
    setIsInitialized(true);
    console.log('✅ Scandit initialized successfully');
    
  } catch (error: any) {
    console.error('❌ Scandit initialization error:', error);
    setError(`Initialization failed: ${error.message}`);
    setInitializationStep('❌ Initialization failed');
    setIsInitialized(false);
  }
};

export const waitForVideoContainer = async (
  videoRef: React.RefObject<HTMLDivElement>,
  maxAttempts = 50
) => {
  let attempts = 0;
  while (!videoRef.current && attempts < maxAttempts) {
    console.log(`Waiting for video container... attempt ${attempts + 1}`);
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }
  
  if (!videoRef.current) {
    throw new Error('Video container not available after waiting');
  }
  
  console.log('✅ Video container is now available');
  return videoRef.current;
};
